import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Info, Plus, Sparkles, Trash2, Wand2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select, Textarea, Toggle } from '@/components/ui/Input';
import { FolderPicker } from '@/components/storage/FolderPicker';
import { PolaNama } from './PolaNama';
import { FormAkunAi } from '@/components/ai/FormAkunAi';
import { useToast } from '@/components/ui/Toast';

const TIPE_KOLOM = [
  { value: 'text', label: 'Teks' },
  { value: 'number', label: 'Angka' },
  { value: 'link', label: 'Tautan' },
  { value: 'image', label: 'Gambar' },
  { value: 'date', label: 'Tanggal' },
];

/**
 * Penjelasan mesin crawl dalam bahasa manusia. Ditulis karena pemilik proyek
 * sendiri menyebut bagian ini membingungkan; kalau dia bingung, user juga.
 */
const PenjelasanMesin = () => (
  <div className="flex gap-3 rounded-lg bg-[var(--surface-sunken)] p-3 text-xs text-[var(--text-secondary)]">
    <Info className="mt-0.5 size-4 shrink-0 text-[var(--text-muted)]" aria-hidden="true" />
    <div className="space-y-1">
      <p>
        <b>Mesin crawl</b> adalah cara Crawel membuka halaman. <b>Cepat</b> membaca
        HTML mentah: hemat dan kilat, tapi tidak melihat konten yang dimuat JavaScript.
        <b> Browser</b> membuka halaman seperti Chrome sungguhan: lebih lambat, tapi melihat
        semuanya.
      </p>
      <p>
        Kalau ragu, biarkan <b>Otomatis</b>: Crawel mencoba yang cepat dulu, dan beralih ke
        browser hanya bila halamannya terlihat kosong.
      </p>
    </div>
  </div>
);

/**
 * Kartu ajakan menambah akun AI, tampil selama pengguna belum punya satu pun.
 *
 * Dulu isinya satu kolom kunci mentah yang langsung membuat akun bernama
 * "Kunci pertama" dengan penyedia bawaan. Akibatnya akun yang lahir dari sini
 * tidak punya nama maupun model pilihan pengguna, dan baru ketahuan saat
 * membuka Pengaturan. Sekarang yang dibuka adalah formulir yang sama persis
 * dengan yang ada di Pengaturan, jadi akun apa pun yang dibuat di mana pun
 * berbentuk sama.
 */
const AjakanAkunAi = ({ onTambah }) => (
  <div className="rounded-xl border border-warning-200 bg-warning-50 p-4 dark:border-warning-500/25 dark:bg-warning-500/10">
    <p className="text-sm font-semibold text-[var(--text-primary)]">
      Mode AI memakai akun penyedia Anda sendiri
    </p>
    <p className="mt-1 text-xs leading-relaxed text-[var(--text-secondary)]">
      Penamaan kolom dikerjakan model AI memakai kunci Anda, bukan kunci Crawel. Isi halaman
      tidak pernah dikirim ke penyedia mana pun, hanya nama tebakan kolomnya. Akunnya bisa
      lebih dari satu dan dipilih per pekerjaan.
    </p>
    <Button size="sm" icon={Plus} className="mt-3" onClick={onTambah}>
      Tambah akun AI
    </Button>
  </div>
);

/**
 * Mode Otomatis dan mode AI pada halaman Buat Scraping.
 *
 * Formulirnya sengaja hanya lima isian: nama, URL, deskripsi, mesin, format.
 * Semua kerumitan selektor disembunyikan - hasil deteksi tampil sebagai
 * daftar kolom yang bisa disunting plus pratinjau JSON, lalu jadi job utuh
 * saat disimpan. Mode AI identik, hanya deteksinya dibantu model bahasa
 * dengan kunci milik user.
 */
export const SimpleMode = ({ ai = false }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Batas paket menentukan boleh atau tidaknya mematikan robots.txt. Dibaca
  // dari potret pemakaian yang sama dengan halaman Penagihan, supaya
  // penimpaan admin dan add-on ikut terhitung.
  const { data: pemakaian } = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.get('/dashboard/usage'),
  });
  const bolehAturRobots = Boolean(pemakaian?.data?.features?.robotsOpsional);
  const namaPaket = pemakaian?.data?.plan?.name ?? 'ini';

  const [form, setForm] = useState({
    name: '',
    targetUrl: '',
    description: '',
    engine: 'auto',
    respectRobotsTxt: true,
    outputFormat: 'table',
  });
  const [deteksi, setDeteksi] = useState(null); // { itemSelector, pagination, itemCount }
  const [kolom, setKolom] = useState([]);
  const [jadwal, setJadwal_] = useState({
    type: 'once',
    enabled: false,
    timeOfDay: '09:00',
    dayOfWeek: 1,
    dayOfMonth: 1,
    namePattern: '',
    autoSave: true,
    outputFormat: 'csv',
    folderId: '',
  });

  // Akun AI milik pengguna. Boleh lebih dari satu, dan yang dipilih di sini
  // menentukan penyedia mana yang dipakai untuk deteksi ini saja.
  const { data: dataAkunAi, refetch: cekKunci } = useQuery({
    queryKey: ['ai-accounts'],
    queryFn: () => api.get('/me/ai-accounts'),
    enabled: ai,
  });
  const akunAi = (dataAkunAi?.data ?? []).filter((a) => a.aktif);
  const kunciSiap = !ai || akunAi.length > 0;

  // Kosong berarti "pakai akun utama". Nilainya sengaja tidak diisi otomatis
  // dengan id akun utama: kalau pengguna kemudian mengganti akun utamanya di
  // Pengaturan, pilihan yang sudah tersimpan di sini tidak ikut berubah dan
  // itu mengejutkan.
  const [akunAiDipilih, setAkunAiDipilih] = useState('');
  const [tambahAkun, setTambahAkun] = useState(false);

  /**
   * Menyimpan akun AI baru tanpa meninggalkan halaman ini.
   *
   * Akun yang baru dibuat langsung dipilih untuk deteksi berikutnya. Membuat
   * akun lalu tetap memakai akun lama adalah hasil yang mengejutkan: orang
   * menambah akun justru karena ingin memakainya sekarang.
   */
  const simpanAkunAi = useMutation({
    mutationFn: (body) => api.post('/me/ai-accounts', body),
    onSuccess: async (res) => {
      setTambahAkun(false);
      await cekKunci();
      queryClient.invalidateQueries({ queryKey: ['ai-accounts'] });
      if (res?.data?.id && !res.data.utama) setAkunAiDipilih(res.data.id);
      toast.success('Akun AI tersimpan', {
        description: 'Deteksi mode AI sekarang bisa memakainya.',
      });
    },
    onError: (err) =>
      toast.error('Gagal menyimpan akun AI', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const analisa = useMutation({
    mutationFn: () =>
      api.post('/jobs/analyze', {
        targetUrl: form.targetUrl,
        engine: form.engine,
        useAi: ai,
        // Deteksi mengambil halamannya juga, jadi ia harus tunduk pada
        // sakelar yang sama. Tanpa ini, mematikan kepatuhan robots.txt tidak
        // berpengaruh apa pun di langkah deteksi dan sakelarnya terlihat rusak.
        respectRobotsTxt: form.respectRobotsTxt,
        ...(ai && akunAiDipilih ? { aiAccountId: akunAiDipilih } : {}),
      }),
    onSuccess: (res) => {
      const kandidat = res.data?.candidates?.[0];
      if (!kandidat) {
        toast.error('Tidak ditemukan daftar berulang', {
          description: res.data?.message ?? 'Coba mesin Browser, atau pakai mode Manual.',
        });
        return;
      }
      setDeteksi({
        itemSelector: kandidat.itemSelector,
        pagination: res.data.pagination ?? null,
        itemCount: kandidat.itemCount,
      });
      setKolom(
        kandidat.fields.map((f) => ({
          name: f.name,
          selector: f.selector,
          type: TIPE_KOLOM.some((t) => t.value === f.type) ? f.type : 'text',
          contoh: (f.samples ?? [])[0] ?? '',
        })),
      );
      if (res.data.suggestedTitle && !form.name) {
        setForm((prev) => ({ ...prev, name: res.data.suggestedTitle }));
      }
      toast.success(`${kandidat.itemCount} item terdeteksi`, {
        description: 'Periksa kolomnya di bawah, sunting bila perlu, lalu simpan.',
      });
    },
    onError: (err) =>
      toast.error('Deteksi gagal', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const simpan = useMutation({
    mutationFn: (runNow) =>
      api.post('/jobs', {
        name: form.name,
        description: form.description || '',
        targetUrl: form.targetUrl,
        engine: form.engine,
        outputFormat: form.outputFormat,
        deduplicate: true,
        crawl: {
          depth: 0,
          maxPages: 50,
          delayMs: 1000,
          respectRobotsTxt: form.respectRobotsTxt,
          itemSelector: deteksi?.itemSelector ?? null,
          paginationSelector: deteksi?.pagination ?? null,
        },
        fields: kolom
          .filter((k) => k.name.trim() && k.selector.trim())
          .map((k) => ({ name: k.name, selector: k.selector, type: k.type, attribute: null })),
        schedule: {
          ...jadwal,
          enabled: jadwal.enabled && jadwal.type !== 'once',
          folderId: jadwal.folderId || null,
        },
        runNow,
      }),
    onSuccess: (res, runNow) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast.success(runNow ? 'Crawl dimulai' : 'Job tersimpan');
      navigate(`/jobs/${res.data.id}`);
    },
    onError: (err) =>
      toast.error('Job tidak bisa disimpan', {
        description: err instanceof ApiError ? err.message : 'Periksa isian lalu coba lagi.',
      }),
  });

  const set = (kunci, nilai) => setForm((prev) => ({ ...prev, [kunci]: nilai }));
  const setJadwal = (kunci, nilai) => setJadwal_((p) => ({ ...p, [kunci]: nilai }));

  /**
   * Deteksi berjalan sendiri begitu URL berhenti diketik selama satu detik.
   *
   * Tombolnya tetap ada untuk mencoba ulang, tetapi menunggu pengguna
   * menekannya membuat mode ini tidak lebih ringan daripada Manual - padahal
   * justru itu alasan mode ini ada.
   *
   * `terakhirDideteksi` mencegah URL yang sama dideteksi berulang kali saat
   * komponen dirender ulang karena sebab lain.
   */
  const terakhirDideteksi = useRef('');
  useEffect(() => {
    const url = form.targetUrl.trim();
    if (!/^https?:\/\/\S+\.\S+/.test(url)) return undefined;
    if (url === terakhirDideteksi.current) return undefined;
    if (ai && !kunciSiap) return undefined;

    const jeda = setTimeout(() => {
      terakhirDideteksi.current = url;
      analisa.mutate();
    }, 1000);
    return () => clearTimeout(jeda);
  }, [form.targetUrl, form.engine, ai, kunciSiap]); // eslint-disable-line react-hooks/exhaustive-deps
  const setKolomKe = (i, baru) => setKolom((prev) => prev.map((k, idx) => (idx === i ? baru : k)));

  // Pratinjau JSON: satu objek contoh yang dibangun dari kolom terdeteksi,
  // persis bentuk baris data yang nanti dihasilkan crawl.
  const contohJson = Object.fromEntries(
    kolom.filter((k) => k.name.trim()).map((k) => [k.name, k.contoh || '...']),
  );

  const siapSimpan =
    form.name.trim().length >= 3 &&
    form.targetUrl.trim() &&
    kolom.some((k) => k.name.trim() && k.selector.trim());

  return (
    <div className="space-y-4">
      {ai && !kunciSiap && <AjakanAkunAi onTambah={() => setTambahAkun(true)} />}

      <Card>
        <CardHeader
          title={ai ? 'Scraping dengan AI' : 'Scraping Otomatis'}
          description={
            ai
              ? 'Tempel URL, biarkan deteksi plus AI menamai kolomnya. Hasilnya langsung bisa dipakai tanpa pindah-pindah.'
              : 'Tempel URL, tekan Deteksi, sunting kolom bila perlu. Tanpa selektor, tanpa konfigurasi.'
          }
        />
        <CardBody className="space-y-4">
          {/* Pemilih akun AI, plus jalan menambah akun tanpa pindah halaman.

              Daftarnya muncul sejak ada satu akun, bukan dua. Dulu dengan satu
              akun tidak ada apa pun di layar, jadi tidak ada tempat untuk
              menaruh tombol tambah dan satu-satunya cara menambah akun kedua
              adalah menebak bahwa jalurnya ada di Pengaturan. */}
          {ai && akunAi.length > 0 && (
            <Field
              label="Akun AI"
              hint={
                akunAiDipilih
                  ? akunAi.find((a) => a.id === akunAiDipilih)?.deskripsi || undefined
                  : 'Memakai akun utama Anda.'
              }
            >
              {/* Tombol tambah berada di DALAM Field, sebaris dengan Select.

                  Sebelumnya keduanya disandingkan di luar Field dengan
                  `items-end`, dan itu meratakan tombol ke dasar Field yang
                  mencakup barisan hint di bawah input. Akibatnya tombol turun
                  sendiri dan tidak pernah sejajar dengan dropdownnya. Sebagai
                  saudara sebaris seperti ini, keduanya pasti setinggi. */}
              {(props) => (
                <div className="flex gap-2">
                  <Select
                    {...props}
                    value={akunAiDipilih}
                    onChange={(e) => setAkunAiDipilih(e.target.value)}
                    className="min-w-0 flex-1"
                  >
                    <option value="">
                      Akun utama ({akunAi.find((a) => a.utama)?.nama ?? akunAi[0]?.nama})
                    </option>
                    {akunAi.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.nama}
                        {a.model ? ` - ${a.model}` : ''}
                      </option>
                    ))}
                  </Select>
                  <Button
                    variant="secondary"
                    icon={Plus}
                    onClick={() => setTambahAkun(true)}
                    className="shrink-0"
                  >
                    Tambah
                  </Button>
                </div>
              )}
            </Field>
          )}

          <Field label="Nama Job" required>
            {(props) => (
              <Input
                {...props}
                value={form.name}
                placeholder="Misal: Harga Kompetitor"
                onChange={(e) => set('name', e.target.value)}
              />
            )}
          </Field>

          <Field
            label="URL"
            required
            hint="Deteksi berjalan sendiri sesaat setelah Anda selesai menempel alamatnya."
          >
            {(props) => (
              <div className="flex gap-2">
                <Input
                  {...props}
                  type="url"
                  value={form.targetUrl}
                  placeholder="https://contoh.com/produk"
                  className="flex-1"
                  onChange={(e) => set('targetUrl', e.target.value)}
                />
                <Button
                  icon={ai ? Sparkles : Wand2}
                  variant={deteksi ? 'secondary' : 'primary'}
                  loading={analisa.isPending}
                  disabled={!form.targetUrl.trim() || (ai && !kunciSiap)}
                  onClick={() => {
                    terakhirDideteksi.current = form.targetUrl.trim();
                    analisa.mutate();
                  }}
                >
                  {analisa.isPending
                    ? 'Mendeteksi...'
                    : deteksi
                      ? 'Deteksi Ulang'
                      : ai
                        ? 'Deteksi + AI'
                        : 'Deteksi Otomatis'}
                </Button>
              </div>
            )}
          </Field>

          <Field label="Deskripsi">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={form.description}
                placeholder="Untuk apa data ini dipakai? (opsional)"
                onChange={(e) => set('description', e.target.value)}
              />
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Mesin Crawl">
              {(props) => (
                <Select
                  {...props}
                  value={form.engine}
                  onChange={(e) => set('engine', e.target.value)}
                  options={[
                    { value: 'auto', label: 'Otomatis (disarankan)' },
                    { value: 'static', label: 'Cepat - HTML statis' },
                    { value: 'dynamic', label: 'Browser Chrome - halaman JavaScript' },
                    { value: 'playwright', label: 'Browser Playwright - kalau Chrome gagal' },
                  ]}
                />
              )}
            </Field>
            <Field label="Format Output">
              {(props) => (
                <Select
                  {...props}
                  value={form.outputFormat}
                  onChange={(e) => set('outputFormat', e.target.value)}
                  options={[
                    { value: 'table', label: 'Tabel terstruktur' },
                    { value: 'json', label: 'JSON' },
                    { value: 'csv', label: 'CSV' },
                  ]}
                />
              )}
            </Field>
          </div>
          {/* Kepatuhan robots.txt. Ditempatkan di sini, bukan hanya di mode
              manual, karena hampir semua orang memakai mode otomatis dan
              sakelar yang cuma ada di mode lain sama saja dengan tidak ada. */}
          <div className="rounded-lg border border-[var(--border-subtle)] p-4">
            <Toggle
              checked={bolehAturRobots ? form.respectRobotsTxt : true}
              onChange={(v) => set('respectRobotsTxt', v)}
              disabled={!bolehAturRobots}
              label="Hormati robots.txt situs sumber"
              description={
                bolehAturRobots
                  ? 'Sebagian situs melarang seluruh crawler lewat robots.txt, termasuk untuk data yang pemiliknya sendiri persilakan diambil. Matikan hanya kalau Anda memang berhak mengambil data itu.'
                  : `Paket ${namaPaket} wajib menghormati robots.txt. Naik ke Growth atau lebih tinggi untuk bisa mengaturnya sendiri.`
              }
            />
            {bolehAturRobots && !form.respectRobotsTxt && (
              <p className="mt-3 flex items-start gap-2 rounded-lg bg-warning-50 p-3 text-xs leading-relaxed text-warning-700 dark:bg-warning-500/10 dark:text-warning-500">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                <span>
                  Anda mematikan penjagaan yang dipasang pemilik situs. Pastikan Anda memang berhak
                  mengambil data ini, dan biarkan jeda antar permintaan tetap wajar supaya situsnya
                  tidak terbebani. Tanggung jawabnya ada pada Anda.
                </span>
              </p>
            )}
          </div>


          <PenjelasanMesin />
        </CardBody>
      </Card>

      {deteksi && (
        <Card>
          <CardHeader
            title={`Kolom Terdeteksi (${deteksi.itemCount} item)`}
            description="Sunting nama dan tipenya, buang yang tidak perlu, atau tambah kolom baru."
            action={
              <Button
                variant="secondary"
                size="sm"
                icon={Plus}
                onClick={() =>
                  setKolom((prev) => [...prev, { name: '', selector: '', type: 'text', contoh: '' }])
                }
              >
                Tambah Kolom
              </Button>
            }
          />
          <CardBody className="space-y-3">
            {kolom.map((k, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <Input
                  value={k.name}
                  aria-label={`Nama kolom ${i + 1}`}
                  placeholder="Nama kolom"
                  className="h-10 w-40 px-3 text-sm font-semibold"
                  onChange={(e) => setKolomKe(i, { ...k, name: e.target.value })}
                />
                <div className="w-32">
                  <Select
                    value={k.type}
                    aria-label={`Tipe kolom ${i + 1}`}
                    options={TIPE_KOLOM}
                    onChange={(e) => setKolomKe(i, { ...k, type: e.target.value })}
                  />
                </div>
                <Input
                  value={k.selector}
                  aria-label={`Selektor kolom ${i + 1}`}
                  placeholder="selektor (terisi otomatis)"
                  className="h-10 min-w-40 flex-1 px-3 font-mono text-xs"
                  onChange={(e) => setKolomKe(i, { ...k, selector: e.target.value })}
                />
                <span className="hidden max-w-40 truncate text-xs text-[var(--text-muted)] lg:inline">
                  {k.contoh}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Trash2}
                  iconOnly
                  aria-label={`Hapus kolom ${i + 1}`}
                  onClick={() => setKolom((prev) => prev.filter((_, idx) => idx !== i))}
                />
              </div>
            ))}

            <div>
              <p className="mb-1.5 text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
                Contoh keluaran (JSON)
              </p>
              <pre className="overflow-x-auto rounded-lg bg-[var(--surface-sunken)] p-3 font-mono text-xs text-[var(--text-secondary)]">
                {JSON.stringify(contohJson, null, 2)}
              </pre>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Penjadwalan"
          description="Biarkan Sekali jalan kalau Anda cuma butuh sekali. Jadwal bisa diubah kapan saja lewat halaman Scheduler."
        />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Frekuensi">
              {(props) => (
                <Select
                  {...props}
                  value={jadwal.type}
                  onChange={(e) => setJadwal('type', e.target.value)}
                  options={[
                    { value: 'once', label: 'Sekali jalan (tanpa jadwal)' },
                    { value: 'hourly', label: 'Setiap jam' },
                    { value: 'daily', label: 'Harian' },
                    { value: 'weekly', label: 'Mingguan' },
                    { value: 'monthly', label: 'Bulanan' },
                  ]}
                />
              )}
            </Field>
            {jadwal.type !== 'once' && (
              <Field label="Jam" hint="Waktu Asia/Jakarta">
                {(props) => (
                  <Input
                    {...props}
                    type="time"
                    value={jadwal.timeOfDay}
                    onChange={(e) => setJadwal('timeOfDay', e.target.value)}
                  />
                )}
              </Field>
            )}
          </div>

          {jadwal.type === 'weekly' && (
            <Field label="Hari">
              {(props) => (
                <Select
                  {...props}
                  value={String(jadwal.dayOfWeek)}
                  onChange={(e) => setJadwal('dayOfWeek', Number(e.target.value))}
                  options={['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'].map(
                    (h, i) => ({ value: String(i), label: h }),
                  )}
                />
              )}
            </Field>
          )}

          {jadwal.type === 'monthly' && (
            <Field label="Tanggal">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min="1"
                  max="31"
                  value={jadwal.dayOfMonth}
                  onChange={(e) => setJadwal('dayOfMonth', Number(e.target.value))}
                />
              )}
            </Field>
          )}

          {jadwal.type !== 'once' && (
            <>
              <Toggle
                checked={jadwal.enabled}
                onChange={(v) => setJadwal('enabled', v)}
                label="Aktifkan jadwal sekarang"
                description="Job mulai berjalan sendiri sesuai frekuensi di atas."
              />
              <Toggle
                checked={jadwal.autoSave}
                onChange={(v) => setJadwal('autoSave', v)}
                label="Simpan hasil otomatis"
                description="Tiap jalan menyimpan hasilnya ke Penyimpanan Pribadi."
              />

              {jadwal.autoSave && (
                <>
                  <PolaNama
                    nilai={jadwal.namePattern}
                    tipe={jadwal.type}
                    namaJob={form.name}
                    onUbah={(v) => setJadwal('namePattern', v)}
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Format berkas">
                      {(props) => (
                        <Select
                          {...props}
                          value={jadwal.outputFormat}
                          onChange={(e) => setJadwal('outputFormat', e.target.value)}
                          options={[
                            { value: 'csv', label: 'CSV' },
                            { value: 'json', label: 'JSON' },
                            { value: 'ndjson', label: 'NDJSON' },
                          ]}
                        />
                      )}
                    </Field>
                    <Field label="Folder tujuan">
                      {(props) => (
                        <FolderPicker
                          {...props}
                          value={jadwal.folderId}
                          onChange={(v) => setJadwal('folderId', v)}
                        />
                      )}
                    </Field>
                  </div>
                </>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          disabled={!siapSimpan}
          loading={simpan.isPending && simpan.variables === false}
          onClick={() => simpan.mutate(false)}
        >
          Simpan Draf
        </Button>
        <Button
          disabled={!siapSimpan}
          loading={simpan.isPending && simpan.variables === true}
          onClick={() => simpan.mutate(true)}
        >
          Simpan & Jalankan
        </Button>
      </div>
      {/* Formulir yang sama persis dengan yang ada di Pengaturan > AI. */}
      <Modal
        open={tambahAkun}
        onClose={() => setTambahAkun(false)}
        size="lg"
        title="Tambah akun AI"
      >
        <FormAkunAi
          menyimpan={simpanAkunAi.isPending}
          onBatal={() => setTambahAkun(false)}
          onSimpan={(body) => simpanAkunAi.mutate(body)}
        />
      </Modal>

    </div>
  );
};
